import { useCallback, useEffect, useState } from "react";
import { Copy, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

// Scoped key of the E3 reviewer agent (header X-Atelier-Key): shown once, stored hashed, revocable.
export const AtelierKeys = () => {
  const { t, fmtDate } = useI18n();
  const [keys, setKeys] = useState([]);
  const [fresh, setFresh] = useState(null);
  const load = useCallback(() => api.get("/admin/atelier/keys").then(({ data }) => setKeys(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try { const { data } = await api.post("/admin/atelier/keys", {}); setFresh(data.key); load(); } catch (e) { toast.error(translateError(e, t)); }
  };
  const revoke = async (id) => { await api.delete(`/admin/atelier/keys/${id}`); load(); };
  return (
    <section className="card-carbon min-w-0 space-y-3 rounded-2xl p-4" data-testid="atelier-keys">
      <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-[var(--gold-bright)]"><KeyRound className="h-4 w-4" /> {t("atelier.keys")}</h2>
      <p className="break-words text-xs text-neutral-400">{t("atelier.keysHint")}</p>
      <button type="button" className="btn-gold btn-sm" onClick={create} data-testid="atelier-key-create"><KeyRound className="h-4 w-4" /> {t("atelier.keyCreate")}</button>
      {fresh && (
        <div className="min-w-0 space-y-1 rounded-xl border border-[var(--gold)] bg-black/50 p-3" data-testid="atelier-key-fresh">
          <p className="text-xs font-semibold text-amber-200">{t("atelier.keyOnce")}</p>
          <div className="flex min-w-0 items-center gap-2">
            <code className="min-w-0 flex-1 break-all text-xs text-white" dir="ltr" data-testid="atelier-key-value">{fresh}</code>
            <button type="button" className="btn-red btn-sm" onClick={() => navigator.clipboard?.writeText(fresh)} aria-label="copy" data-testid="atelier-key-copy"><Copy className="h-4 w-4" /></button>
          </div>
        </div>
      )}
      <ul className="min-w-0 space-y-1 text-xs">
        {keys.map((k) => (
          <li key={k.id} className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-neutral-300" data-testid={`atelier-key-${k.id}`}>
            <code dir="ltr">{k.prefix}…</code><span>{k.label}</span><span className="text-neutral-500">{fmtDate(k.created_at)}</span>
            {k.last_used && <span className="text-neutral-500">{t("atelier.lastUsed")} {fmtDate(k.last_used)}</span>}
            {k.revoked_at ? <span className="text-red-300">{t("atelier.revoked")}</span> : (
              <button type="button" className="inline-flex items-center gap-1 text-red-300 hover:underline" onClick={() => revoke(k.id)} data-testid={`atelier-key-revoke-${k.id}`}><Trash2 className="h-3.5 w-3.5" /> {t("atelier.revoke")}</button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};
